import { useCallback, useRef, useState } from "react";

// Lightweight history for a piece of state. Supports:
// - set(next): replaces current state; pushes previous to `past`, clears `future`
// - setSilent(next): replaces current state without touching history (for drag frames)
// - checkpoint(): explicitly push current state onto past (call before a burst
//   of setSilent, e.g., at pointer-down of a drag)
// - undo() / redo(): pop from past/future as usual
// - reset(next): wipes history and sets state to `next`
export const useHistoryState = (initial) => {
  const [state, setState] = useState(initial);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  const set = useCallback((next) => {
    setState((cur) => {
      const nx = typeof next === "function" ? next(cur) : next;
      pastRef.current = [...pastRef.current, cur].slice(-100);
      futureRef.current = [];
      rerender();
      return nx;
    });
  }, []);

  const setSilent = useCallback((next) => {
    setState((cur) => (typeof next === "function" ? next(cur) : next));
  }, []);

  const checkpoint = useCallback(() => {
    setState((cur) => {
      pastRef.current = [...pastRef.current, cur].slice(-100);
      futureRef.current = [];
      rerender();
      return cur;
    });
  }, []);

  const undo = useCallback(() => {
    if (pastRef.current.length === 0) return;
    setState((cur) => {
      const prev = pastRef.current[pastRef.current.length - 1];
      pastRef.current = pastRef.current.slice(0, -1);
      futureRef.current = [cur, ...futureRef.current];
      rerender();
      return prev;
    });
  }, []);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    setState((cur) => {
      const nxt = futureRef.current[0];
      futureRef.current = futureRef.current.slice(1);
      pastRef.current = [...pastRef.current, cur];
      rerender();
      return nxt;
    });
  }, []);

  const reset = useCallback((next) => {
    pastRef.current = [];
    futureRef.current = [];
    setState(next);
    rerender();
  }, []);

  return {
    state,
    set,
    setSilent,
    checkpoint,
    undo,
    redo,
    reset,
    canUndo: pastRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
  };
};
